/**
 * `CertificationCard` — card di una certificazione posseduta da un utente.
 *
 * Presentazionale e riusabile: mostra immagine/titolo, ente e date
 * (conseguimento/scadenza con evidenza sullo stato), le skill collegate come
 * chip e un link‑icona al badge Credly. Usata nella pagina personale
 * ("Le mie certificazioni") e nel blocco Certificazioni della dashboard team.
 *
 * `skillNameById` risolve gli id skill in nomi per i chip; se assente (o se un
 * id non è risolvibile) il chip non viene mostrato.
 */
import type { UserCertificationDto } from '../shared/dto';
import { Badge, Card, Icon } from './ui';

/** Stato di validità di una certificazione in base alla data di scadenza. */
type ExpiryStatus = 'none' | 'valid' | 'expiring' | 'expired';

/** Giorni entro cui una certificazione è considerata "in scadenza". */
const EXPIRING_SOON_DAYS = 60;

/**
 * Immagine di fallback mostrata quando una certificazione non ha un badge/
 * immagine propri (`imageUrl` assente). Vive in `public/imgs`.
 */
export const CERTIFICATION_PLACEHOLDER_IMAGE = '/imgs/certification-placeholder.png';

/** Formatta una data ISO in `gg/mm/aaaa`, o placeholder se assente. */
export function formatCertDate(iso: string | null): string {
  if (iso === null) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  return d.toLocaleDateString('it-IT');
}

/** Determina lo stato di scadenza a partire dalla data ISO (o `null`). */
export function certExpiryStatus(iso: string | null): ExpiryStatus {
  if (iso === null) return 'none';
  const expires = new Date(iso);
  if (Number.isNaN(expires.getTime())) return 'none';
  const now = Date.now();
  const diffDays = (expires.getTime() - now) / (1000 * 60 * 60 * 24);
  if (diffDays < 0) return 'expired';
  if (diffDays <= EXPIRING_SOON_DAYS) return 'expiring';
  return 'valid';
}

/**
 * Timestamp (ms) della data di conseguimento di una certificazione, usato per
 * ordinare le liste dalla più recente alla più datata. Gestisce sia le date ISO
 * (`issuedAt`) sia il testo libero del CV (`date`), parsando in best-effort con
 * `Date.parse`. Ritorna `null` se la data è assente o non interpretabile, così
 * la voce finisce in coda.
 */
export function certIssuedTime(raw: string | null | undefined): number | null {
  if (raw === null || raw === undefined || raw.trim() === '') return null;
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

/**
 * Ordina una lista di certificazioni per data di conseguimento DECRESCENTE (le
 * più recenti prima). Le voci senza una data valida vanno in coda, con
 * ordinamento alfabetico stabile per nome come tie-break. Non muta l'input.
 *
 * Generica sui dati: il chiamante fornisce come leggere la data (`getIssued`) e
 * il nome (`getName`) da ciascun elemento, così la stessa logica si applica a
 * `UserCertificationDto`, `ExternalCvCertificationDto`, `EuropassCertification`,
 * ecc. a prescindere dai nomi dei campi.
 */
export function sortCertificationsByIssuedDesc<T>(
  rows: readonly T[],
  getIssued: (row: T) => string | null | undefined,
  getName: (row: T) => string,
): T[] {
  return [...rows].sort((a, b) => {
    const ta = certIssuedTime(getIssued(a));
    const tb = certIssuedTime(getIssued(b));
    // Entrambe con data: più recente prima.
    if (ta !== null && tb !== null && ta !== tb) return tb - ta;
    // Solo una ha la data: quella con data precede.
    if (ta !== null && tb === null) return -1;
    if (ta === null && tb !== null) return 1;
    // Nessuna data (o pari): ordinamento alfabetico stabile per nome.
    return getName(a).localeCompare(getName(b), 'it', { sensitivity: 'base' });
  });
}

interface CertificationCardProps {
  certification: UserCertificationDto;
  /** Risolutore id skill → nome per i chip (assente ⇒ nessun chip). */
  skillNameById?: (id: string) => string | undefined;
  /**
   * Quando `true`, mostra in alto a destra un'icona "link esterno" puramente
   * decorativa (nessuna interazione propria): usata nelle liste dove l'intera
   * card è cliccabile tramite uno stretched-link esterno, così l'icona segue il
   * sollevamento della card in hover. Ignorata se la card ha già il link al
   * badge Credly (`badgeUrl`).
   */
  showOpenIcon?: boolean;
  /**
   * Callback di rimozione: se fornita, mostra un cestino rosso in basso a destra
   * della card (dentro la `.ui-card`, così segue il sollevamento in hover). Il
   * chiamante gestisce la conferma (es. un modale). Assente ⇒ nessun cestino.
   */
  onRemove?: () => void;
  /** Etichetta accessibile del cestino di rimozione (default generico). */
  removeLabel?: string;
}

export function CertificationCard({
  certification: c,
  skillNameById,
  showOpenIcon = false,
  onRemove,
  removeLabel,
}: CertificationCardProps) {
  // Le certificazioni scadute non contano più come "collegamento attivo" a una
  // skill: non mostriamo i chip delle skill collegate (né concorrono al bonus,
  // lato backend). La card resta visibile con lo stato "Scaduta" per trasparenza.
  const isExpired = certExpiryStatus(c.expiresAt) === 'expired';
  const skillChips =
    skillNameById === undefined || isExpired
      ? []
      : c.skillIds
          .map((id) => ({ id, name: skillNameById(id) }))
          .filter((s): s is { id: string; name: string } => s.name !== undefined);
  const hasFooter =
    skillChips.length > 0 || c.source === 'credly' || c.source === 'linkedin';

  return (
    <Card>
      {c.badgeUrl !== undefined ? (
        <a
          href={c.badgeUrl}
          target="_blank"
          rel="noopener noreferrer"
          className="certification-card__badge-link"
          aria-label={`Vedi il badge di ${c.name} su Credly`}
          title="Vedi badge"
        >
          <Icon name="external" />
        </a>
      ) : (
        showOpenIcon && (
          <span
            className="certification-card__badge-link certification-card__open-icon"
            aria-hidden="true"
          >
            <Icon name="external" />
          </span>
        )
      )}

      {onRemove !== undefined && (
        <button
          type="button"
          className="certification-card__remove"
          aria-label={removeLabel ?? `Rimuovi ${c.name}`}
          title={removeLabel ?? 'Rimuovi'}
          onClick={onRemove}
        >
          <Icon name="trash" size={18} />
        </button>
      )}

      <header className="certification-card__header">
        <img
          src={c.imageUrl ?? CERTIFICATION_PLACEHOLDER_IMAGE}
          alt=""
          aria-hidden="true"
          className="certification-card__image"
          width={64}
          height={64}
        />
        <div className="certification-card__heading">
          <h3 className="certification-card__name">{c.name}</h3>
        </div>
      </header>

      <hr className="certification-card__divider" />

      <dl className="certification-card__meta">
        {c.issuer !== undefined && (
          <div className="certification-card__meta-item certification-card__meta-item--issuer">
            <dt>Ente</dt>
            <dd>{c.issuer}</dd>
          </div>
        )}
        <div className="certification-card__meta-item">
          <dt>Conseguita</dt>
          <dd>{formatCertDate(c.issuedAt)}</dd>
        </div>
        <div className="certification-card__meta-item">
          <dt>Scadenza</dt>
          <dd>
            {(() => {
              const status = certExpiryStatus(c.expiresAt);
              if (status === 'none') return 'Nessuna';
              if (status === 'expired') {
                return (
                  <Badge tone="danger">{formatCertDate(c.expiresAt)}</Badge>
                );
              }
              if (status === 'expiring') {
                return (
                  <Badge tone="warning">In scadenza {formatCertDate(c.expiresAt)}</Badge>
                );
              }
              return formatCertDate(c.expiresAt);
            })()}
          </dd>
        </div>
      </dl>

      {hasFooter && (
        <>
          <hr className="certification-card__divider certification-card__divider--footer" />
          <footer className="certification-card__footer">
            {c.source === 'credly' && <Badge tone="info">Credly</Badge>}
            {c.source === 'linkedin' && <Badge tone="info">LinkedIn</Badge>}
            {skillChips.map((s) => (
              <Badge key={s.id} tone="neutral">
                {s.name}
              </Badge>
            ))}
          </footer>
        </>
      )}
    </Card>
  );
}

export default CertificationCard;
